/**
 * Shared enums and value types for the CUSP platform.
 *
 * These are the vocabulary of the two spine abstractions (entitlements +
 * content versions) plus the roles/statuses used across bounded contexts.
 * Keep this package dependency-free so any app or package can import it.
 *
 * Re-exported via `./index.ts` alongside the HTTP contracts in `./contracts.ts`.
 */

/** Platform roles. Enforced at the API boundary (RBAC). */
export const ROLES = ['learner', 'faculty', 'institution_admin', 'cusp_admin'] as const;
export type Role = (typeof ROLES)[number];

/**
 * The roles an institution may hand out inside its own tenant.
 *
 * `cusp_admin` is deliberately absent: it is a PLATFORM role, and letting a
 * customer's admin write it into a `memberships` row would be privilege
 * escalation dressed as an ordinary role change. Keeping it out of the wire
 * schema makes that unrepresentable rather than merely guarded.
 */
export const INSTITUTION_ROLES = ['learner', 'faculty', 'institution_admin'] as const;
export type InstitutionRole = (typeof INSTITUTION_ROLES)[number];

/**
 * Which external verifier authenticated an account (issue #89). Deliberately a
 * plain string list, NOT a Postgres enum: the account model is multi-verifier
 * by decision (2026-08-14) — username/password, other social providers and
 * per-school OAuth are all potential later entries — and adding one must be an
 * insert plus a verify route, never a migration. `auth_identities.provider`
 * stores these values as text for the same reason.
 */
export const AUTH_PROVIDERS = ['google', 'prospect'] as const;
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

/** The isolated prospect tutor organization; console suspension is its kill switch. */
export const PROSPECT_DEMO_TENANT_ID = 'f33211f1-2920-4a43-9d83-3266b07e85b2';

/**
 * How a user came to hold an entitlement. All converge to the same learner
 * app — access is checked via the entitlement, never the source.
 * `access_code` is issue #91: a redeemed code granted both the membership and
 * this entitlement in one step (`source_ref` is the code's id).
 */
export const ENTITLEMENT_SOURCES = ['license_seat', 'individual_purchase', 'access_code'] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[number];

export const ENTITLEMENT_STATUSES = ['active', 'expired', 'revoked'] as const;
export type EntitlementStatus = (typeof ENTITLEMENT_STATUSES)[number];

/**
 * WHY A COURSE IS SHUT TO THIS MEMBERSHIP — the greyed-out surface, as a value
 * (issue #13).
 *
 * The demo is "a real tenant with most of the surface off", and the difference
 * between that and a mock wearing the description is that every disabled
 * affordance is DERIVED from rows the tenant does not have. So this vocabulary
 * names states of the entitlement spine, never a UI mode: each reason is a
 * different query answering false, and each one lights up on its own for a real
 * customer without a line of client code changing.
 *
 * Ordered most-specific-first, which is also the precedence the service applies
 * — a fact about THIS learner outranks a fact about their institution:
 *
 *  · `entitlement_revoked`  a grant existed for this user and was taken away
 *  · `entitlement_expired`  a grant existed for this user and ran out
 *  · `no_seat`              the institution licenses the course; nobody has
 *                           assigned this learner a seat on it
 *  · `not_licensed`         the institution holds no active licence covering it
 *
 * There is deliberately no `coming_soon` / `not_built` member. A feature that
 * does not exist is not an access state, and giving the client one enum to
 * branch on for both is how a temporary UI flag ends up load-bearing.
 */
export const CATALOG_LOCK_REASONS = [
  'entitlement_revoked',
  'entitlement_expired',
  /**
   * The learner's grant is fine; the INSTITUTION's licence behind it is not —
   * suspended, revoked, outside its access window, or (for a damaged seat link)
   * no longer resolvable at all.
   *
   * Added because `entitlement_expired` was being reported for all of these,
   * which contradicted itself on screen: "your access has ended" beside a grant
   * whose own status was `active` and whose expiry had not passed. A learner
   * reading that would go looking for a renewal they do not need, when the
   * thing to do is ask their institution.
   */
  'license_inactive',
  'no_seat',
  'not_licensed',
] as const;
export type CatalogLockReason = (typeof CATALOG_LOCK_REASONS)[number];

/** Lifecycle of a course version snapshot. Published snapshots are immutable. */
export const VERSION_STATUSES = ['draft', 'published', 'archived'] as const;
export type VersionStatus = (typeof VERSION_STATUSES)[number];

/** Per-lecture progress state for a learner. */
export const PROGRESS_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export type ProgressStatus = (typeof PROGRESS_STATUSES)[number];

/** Structural class of a lecture component. */
export const COMPONENT_KINDS = ['authored', 'produced', 'exhibit'] as const;
export type ComponentKind = (typeof COMPONENT_KINDS)[number];

/**
 * How a learner is consuming a lecture's media (design `L3` / `L4`).
 *
 * Both modes stream the SAME asset — audio is a rendition the provider serves
 * from one playback id, not a separate upload — which is exactly why
 * `lecture_progress` shares one `resume_ms` between them and why completion
 * reads `video_pct` OR `audio_pct`. A licence names the mode so the player can
 * be told "streaming only, download disabled" for audio without the platform
 * having to model two assets.
 */
export const PLAYBACK_MODES = ['video', 'audio'] as const;
export type PlaybackMode = (typeof PLAYBACK_MODES)[number];

/**
 * Known lecture-component types. The DB `component_types` lookup table is the
 * source of truth (adding a type is an INSERT, not a migration); this list
 * mirrors the reference rows for seeds and compile-time convenience.
 */
export const KNOWN_COMPONENT_TYPES = [
  'script',
  'learning_objectives',
  'study_guide',
  'fact_sheet',
  'quiz',
  'coda_mapping',
  'mastery_question',
  'video',
  'narration',
  'transcript',
  'exhibit_spreadsheet',
  'exhibit_graphic',
  'exhibit_uploaded_file',
  'exhibit_video_link',
  'exhibit_external_url',
] as const;
export type KnownComponentType = (typeof KNOWN_COMPONENT_TYPES)[number];

/**
 * Institution license lifecycle — the states an OPERATOR puts a license in.
 *
 * This list is canonical: `licenses.status` is a `text` column with a CHECK
 * constraint mirroring it, NOT a Postgres enum. That is deliberate (work item
 * 6). A pg enum can be renamed and appended to, but a value can never be
 * removed without building a new type and rewriting every column that uses it,
 * and this vocabulary is young enough that we should be able to take a state
 * back. The cost of the choice is that the constraint and this array can
 * drift, which an enum made impossible — so `policies.test.ts` asserts the
 * database refuses a value that is not in here.
 *
 * `scheduled` is deliberately ABSENT, and its absence is the design rather
 * than an oversight. A license that is active with a start date in the future
 * is scheduled; that is a fact about the clock, not a decision anyone made, so
 * storing it would need something to flip it when the date arrives and would
 * put a second source of truth next to the one that actually gates access
 * (`loadAssignableLicense` already checks the window separately from the
 * status). Surfaces that show a lifecycle to a human derive it — see
 * `LICENSE_STANDINGS` below.
 */
export const LICENSE_STATUSES = ['draft', 'active', 'suspended', 'expired', 'revoked'] as const;
export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

/**
 * What a HUMAN is shown, which is not what the database stores.
 *
 * `licenses.status` holds the five states an operator chooses between
 * (`LICENSE_STATUSES`). `scheduled` is added here and only here, derived from
 * an active license whose `startsAt` has not arrived. That is deliberate: it
 * is a fact about the clock rather than a decision anyone made, nothing in this
 * system runs on a timer to flip it, and the access gate already enforces the
 * date window separately from the status. Storing it would put a second source
 * of truth beside the one that actually gates access, and it would be wrong for
 * every minute between a start date passing and something noticing.
 *
 * `expired` IS stored, and also derived when an active license's `endsAt` has
 * passed — the same reasoning, applied to a value that predates this rule.
 *
 * IT LIVES HERE, BESIDE THE STATUSES IT REINTERPRETS, because two HTTP surfaces
 * now carry it: the platform console's licence list and the institution's own
 * "Access & licence" screen. It started in `@cusp/contracts/platform`, which
 * was right while the console was the only reader and wrong the moment the
 * tenant contract needed the same six words. The derivation itself is NOT here
 * — it needs a clock, and this package is deliberately dependency-free
 * vocabulary; it lives in `apps/api/src/licence-standing.ts`, beside the gate
 * it must agree with.
 */
export const LICENSE_STANDINGS = [
  'draft',
  'scheduled',
  'active',
  'suspended',
  'expired',
  'revoked',
] as const;
export type LicenseStanding = (typeof LICENSE_STANDINGS)[number];

/**
 * The moves a licence may make, from each state.
 *
 * ONE definition, read by the API (which enforces it) and the console (which
 * offers it). It started as a table in the console only, and that was a bug
 * waiting to be found: a UI transition matrix is a suggestion, not an
 * invariant, and the server accepted any status at all — including
 * `active → draft`, which then unlocked course-coverage replacement on a
 * licence whose seats had already derived entitlements from that coverage.
 *
 * The shape of the graph:
 *   · A draft can only go live. It has granted nothing, so there is nothing to
 *     suspend or revoke.
 *   · Nothing returns to `draft`. A draft is the state before a licence has
 *     ever been in force, and going back there would let history be rewritten.
 *   · `expired` can be reactivated — a renewed contract is the common case, and
 *     re-drafting one loses its seats and its history.
 *   · `revoked` is terminal. Closing a contract deliberately should mean
 *     drafting a new one, not quietly resurrecting the old.
 */
export const LICENSE_TRANSITIONS: Readonly<Record<LicenseStatus, readonly LicenseStatus[]>> = {
  draft: ['active'],
  active: ['suspended', 'expired', 'revoked'],
  suspended: ['active', 'revoked'],
  expired: ['active', 'revoked'],
  revoked: [],
};

/** Whether `from → to` is a move a licence may make. A no-op move is legal. */
export function canTransitionLicense(from: LicenseStatus, to: LicenseStatus): boolean {
  return from === to || LICENSE_TRANSITIONS[from].includes(to);
}

/**
 * How an access code stands right now — DERIVED from its stamps, never stored.
 *
 * The precedence is part of the vocabulary and is fixed in one place
 * (`apps/api/src/invite-state.ts`, beside the clock it needs): revoked beats
 * redeemed beats expired beats outstanding. A code can be several of these at
 * once, and without a fixed order two screens rendering the same row disagree.
 *
 * IT LIVES HERE for exactly the reason `LICENSE_STANDINGS` does. It started in
 * `@cusp/contracts/platform`, which was right while the console was the only
 * reader and wrong the moment a SCHOOL's own invite list needed the same four
 * words (migration 0038). Two surfaces inventing two vocabularies for one
 * derived state is the drift a shared list exists to prevent.
 *
 * NOTHING HERE DESCRIBES THE LICENCE A SEAT INVITE NAMES, and there is
 * deliberately no fifth state for "the licence lapsed". A licence can be
 * reactivated, and a stored state could not follow it — the code's own stamps
 * are all this list claims to describe. Redemption is what asks the licence,
 * and it asks at redemption time.
 */
export const ACCESS_CODE_STATES = ['outstanding', 'redeemed', 'expired', 'revoked'] as const;
export type AccessCodeState = (typeof ACCESS_CODE_STATES)[number];

/** Whether an organization is trading normally or has been suspended. */
export const ORG_STATUSES = ['active', 'suspended'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

/**
 * How a question is ANSWERED — one right option, or several.
 *
 * This is the only thing a player needs to know before it can render a
 * question: `multiple_response` means checkboxes and "select all that apply",
 * `multiple_choice` means radio buttons. It is safe to serve, because it says
 * nothing about WHICH options are correct.
 *
 * It replaced `board_style | vignette` (2026-08-19), which described what a
 * question LOOKED like — a guess written before we had seen real content, and
 * a different axis from this one. No player behaviour depended on it, the
 * authoring system never produced either value, and the pilot's 106 ratified
 * questions arrive labelled exactly as below. If "is this a case story?" ever
 * needs recording, it is a second field, added when something reads it.
 */
export const ASSESSMENT_ITEM_FORMATS = ['multiple_choice', 'multiple_response'] as const;
export type AssessmentItemFormat = (typeof ASSESSMENT_ITEM_FORMATS)[number];

/**
 * Rotation state of an assessment item within a course's bank. The authoring
 * system's vocabulary (`question_rotations.status`), adopted verbatim:
 * rotation is a lifecycle above item versions — an item leaves the servable
 * pool by rotating out, never by deletion.
 */
export const ITEM_ROTATION_STATUSES = ['active', 'rotated_out'] as const;
export type ItemRotationStatus = (typeof ITEM_ROTATION_STATUSES)[number];

/**
 * How deeply a lecture covers an accreditation standard (issue #15).
 *
 * A BOOLEAN WOULD BE A LIE, which is why this exists at all: a standard named
 * once in passing and a standard taught and then assessed are the same row in a
 * covered/not-covered model, and an accreditor reading that model is being told
 * something untrue. `introduced | reinforced | assessed` is the vocabulary
 * dental schools already submit in a curriculum map; `mentioned` is the rung
 * below it — publish-contract §6's "touched in passing", which is deliberately
 * NOT coverage.
 *
 * `unspecified` is the rung below even `mentioned`, and it is not a treatment
 * at all — it means an authoring system asserted a MAPPING without asserting a
 * depth. The dashboard's export does exactly that on all 74 of its lecture
 * edges, so the choice was to admit the value or to invent a depth for each
 * one; inventing it would put a coverage claim in front of an accreditor that
 * nobody made. It sits at rank 0 so `isTaught` reports false and any real
 * treatment from another lecture outranks it. An operator fills it in later,
 * by hand, in the editor.
 *
 * ORDERED, weakest first. The index is the comparison key, so a competency
 * covered by several lectures reports the strongest treatment any of them
 * gives it.
 */
export const COVERAGE_DEPTHS = [
  'unspecified',
  'mentioned',
  'introduced',
  'reinforced',
  'assessed',
] as const;
export type CoverageDepth = (typeof COVERAGE_DEPTHS)[number];

/** Position in `COVERAGE_DEPTHS`. Higher is stronger. */
export const coverageDepthRank = (depth: CoverageDepth): number => COVERAGE_DEPTHS.indexOf(depth);

/**
 * The line between touched and taught. A competency whose strongest treatment
 * is `mentioned` reports `taught: false` — for a site visit that is a gap, not
 * a coverage claim, and reporting it as covered is how a binder becomes wrong.
 */
export const TAUGHT_DEPTH_MIN: CoverageDepth = 'introduced';

/** Is this treatment strong enough to claim the standard is taught? */
export const isTaught = (depth: CoverageDepth): boolean =>
  coverageDepthRank(depth) >= coverageDepthRank(TAUGHT_DEPTH_MIN);

/**
 * A pearl's moderation lifecycle (issue #24). The design's promise is
 * "Reviewed before publication · not a comment feed", so `published` is a state
 * a moderator PUTS a row into — submission cannot reach it, and the read path
 * serves nothing else.
 *
 * `rejected` is kept rather than deleted for the same reason a revoked
 * verification is: moderation decisions are a record, and a resubmission of the
 * same text should meet the decision that was already made about it.
 */
export const PEARL_STATUSES = ['submitted', 'published', 'rejected'] as const;
export type PearlStatus = (typeof PEARL_STATUSES)[number];

/**
 * How a question reaches a standard. `direct` is an edge the authoring system
 * asserted on the item itself; `concept` is inferred through the item's concept
 * tags. Both are real answers to "which questions satisfy this standard" and
 * the report says which, rather than silently blending an asserted mapping with
 * an inferred one.
 */
export const STANDARD_LINK_VIAS = ['direct', 'concept'] as const;
export type StandardLinkVia = (typeof STANDARD_LINK_VIAS)[number];

/**
 * The assessment RULES a course version pins (`course_versions.assessment_blueprint`,
 * publish-contract §5.3): the version carries the rules, the item bank supplies
 * the items, so rotation survives a publish.
 *
 * Decided 2026-07-28 (D9): completion is WATCH-BASED and modality-agnostic —
 * `video_pct` OR `audio_pct` crossing `lectureCompletionPct` completes the
 * lecture. The quiz never gates: `passPct` is a reporting threshold ("did they
 * clear the bar"), retakes are unlimited, and every attempt is logged.
 */
export interface AssessmentBlueprint {
  /** Watch % (video OR audio) at which a lecture counts complete — the gate. */
  lectureCompletionPct: number;
  /** Items unlocked with the lecture; the rest unlock on completion. */
  initialItemCount: number;
  /** Reporting threshold for a quiz sitting. Never gates progression. */
  passPct: number;
  /** Unlimited by decision — a cap would contradict the rotating item bank. */
  retakePolicy: 'unlimited';
}

export const DEFAULT_ASSESSMENT_BLUEPRINT: AssessmentBlueprint = {
  lectureCompletionPct: 90,
  initialItemCount: 10,
  passPct: 80,
  retakePolicy: 'unlimited',
};

/**
 * Normalize a stored blueprint (a nullable, possibly partial jsonb) against the
 * defaults. Unknown fields are dropped; missing ones take the platform default,
 * so a version published before a rule existed still gates correctly.
 */
export function resolveAssessmentBlueprint(raw: unknown): AssessmentBlueprint {
  const b = (raw ?? {}) as Partial<AssessmentBlueprint>;
  return {
    lectureCompletionPct:
      typeof b.lectureCompletionPct === 'number'
        ? b.lectureCompletionPct
        : DEFAULT_ASSESSMENT_BLUEPRINT.lectureCompletionPct,
    initialItemCount:
      typeof b.initialItemCount === 'number'
        ? b.initialItemCount
        : DEFAULT_ASSESSMENT_BLUEPRINT.initialItemCount,
    passPct: typeof b.passPct === 'number' ? b.passPct : DEFAULT_ASSESSMENT_BLUEPRINT.passPct,
    retakePolicy: 'unlimited',
  };
}

// ---------------------------------------------------------------------------
// Engagement telemetry (issue #14)
// ---------------------------------------------------------------------------

/**
 * The one event stream, designed for three consumers at once (issue #14):
 * lead scoring, administrator analytics, and the content feedback loop.
 *
 * WHAT IS DELIBERATELY ABSENT: an event for a graded answer. Question
 * performance already has a durable, append-only, RLS'd home in
 * `assessment_attempts`, which pins `(item_key, item_version)` and the sitting
 * ordinal that "of record" is derived from. Duplicating it into the event
 * stream would create a second, weaker copy of an academic record and two
 * numbers that disagree the first time an ingest batch is dropped.
 * `quiz_submitted` therefore carries no score — it marks that a sitting
 * happened, so a visit can be reconstructed, and analytics JOINs the attempts
 * table for what the learner actually answered.
 */
export const ENGAGEMENT_EVENT_KINDS = [
  /** A visit begins. The attribution anchor for lead scoring. */
  'session_started',
  /** A route was entered. Time on site is the sum of these plus heartbeats. */
  'page_view',
  /** Still here. `durationMs` is the interval the client is vouching for. */
  'heartbeat',
  'lecture_opened',
  /** `positionMs` is the playhead; `durationMs` the watched interval. */
  'video_progress',
  /** Completion is modality-agnostic (D9), so audio is its own signal. */
  'audio_progress',
  /** A quiz sitting was submitted. Scores live in `assessment_attempts`. */
  'quiz_submitted',
  'tutor_turn',
  /**
   * The learner clicked "Start chatting" on CuspTutor's first-run disclosure
   * card (contract rule 57). Server-authored by the disclosure route: the
   * consent record IS the point, so it is written once the server has the
   * click, never posted by a client on its own say-so.
   */
  'tutor_disclosure_acknowledged',
  /**
   * A learner reported a tutor answer (contract rule 62). Server-authored by
   * the flag route beside the `tutor_turn_flags` row, so faculty review can
   * read one ledger for "something needs a look".
   */
  'tutor_turn_flagged_needs_review',
] as const;
export type EngagementEventKind = (typeof ENGAGEMENT_EVENT_KINDS)[number];

/**
 * Kinds only the API writes, each beside the row it is the anchor or receipt
 * for: `quiz_submitted` (issue #105) and the two CuspTutor ledger kinds
 * (contract rules 57 and 62). A client-posted copy of any of these would be a
 * record of something the server never saw happen.
 */
export const SERVER_AUTHORED_EVENT_KINDS = [
  'quiz_submitted',
  'tutor_disclosure_acknowledged',
  'tutor_turn_flagged_needs_review',
] as const satisfies readonly EngagementEventKind[];
export type ServerAuthoredEventKind = (typeof SERVER_AUTHORED_EVENT_KINDS)[number];

/**
 * The kinds a CLIENT may post to `/v1/me/events`. Everything above except
 * the server-authored kinds — the type is derived from that list, so a kind
 * added there leaves the client's type with it (PR #279 review).
 *
 * WHY `quiz_submitted` IS SERVER-AUTHORED (issue #105). The row is the idempotency
 * anchor for quiz grading: the submit handler writes it inside the same
 * transaction as the attempt rows, and a later retry is recognised by finding
 * it. If a learner could post one themselves — with a chosen `attemptNumber`
 * and chosen `metadata` — they could pre-place an anchor for a sitting that
 * never happened and steer the grader into its replay path. An anchor a caller
 * can write is not an anchor.
 *
 * The database vocabulary above stays whole; this is the narrower list the
 * wire schema accepts, which is what makes the forgery unrepresentable rather
 * than merely guarded.
 */
export const CLIENT_INGESTABLE_EVENT_KINDS = ENGAGEMENT_EVENT_KINDS.filter(
  (kind) => !(SERVER_AUTHORED_EVENT_KINDS as readonly EngagementEventKind[]).includes(kind),
) as readonly Exclude<EngagementEventKind, ServerAuthoredEventKind>[];
export type ClientIngestableEventKind = (typeof CLIENT_INGESTABLE_EVENT_KINDS)[number];

/**
 * The two lenses on assessment data, which must never be conflated (D9 +
 * issue #14). Unlimited retakes mean an aggregate over ALL attempts trends
 * toward 100% — everyone eventually gets it right — so the very signal the
 * content feedback loop exists to surface disappears into the retake mechanism
 * working.
 *
 * `first_attempt` answers "did they learn it from the lecture" and is the ONLY
 * honest input to a curriculum decision. `latest` answers "where does this
 * learner stand" and belongs to the learner and their transcript. Every number
 * this API returns names which one it is.
 */
export const ASSESSMENT_LENSES = ['first_attempt', 'latest'] as const;
export type AssessmentLens = (typeof ASSESSMENT_LENSES)[number];

/**
 * THE PRIVACY FLOOR for aggregate reporting.
 *
 * The content feedback loop's destination is the authoring dashboard, which has
 * no tenancy, no RLS, and founder-level access to everything in it — correct
 * for an internal tool, and a completely different risk profile the moment a
 * real student row lands there. So the return path is aggregates by
 * construction, and an aggregate over a small enough cell is just a learner row
 * wearing a hat: "1 of 1 students missed this" names a person to anyone who
 * knows the roster.
 *
 * Any cell computed over fewer than this many distinct learners is withheld and
 * counted, never rounded or approximated. Callers may raise the floor; the
 * service refuses to lower it.
 */
export const MIN_AGGREGATE_COHORT = 5;

/**
 * How an at-risk learner is at risk. `A3` is titled "Supportive, not
 * surveillance", and this vocabulary is where that stops being a copy decision:
 * the screen interprets rather than exposes ("likely a scheduling gap, not
 * comprehension"), which is only possible if the signal distinguishes a learner
 * who has stopped showing up from one who is showing up and not understanding.
 *
 * That distinction needs watch AND first-attempt quiz AND recency together —
 * any one of them alone collapses the reading. Best-attempt scores are on-track
 * for nearly everyone once retakes are free, which is why the quiz half is
 * measured on the first-attempt lens.
 */
export const ENGAGEMENT_SIGNALS = ['on_track', 'disengaged', 'struggling', 'both'] as const;
export type EngagementSignal = (typeof ENGAGEMENT_SIGNALS)[number];

/**
 * Machine-readable reasons behind a signal. Keys, not prose: the admin app owns
 * the wording (and its "supportive, not surveillance" framing), the API owns
 * the finding. `quiz_on_track` is not filler — it is the evidence that turns
 * "this student is behind" into "likely a scheduling gap, not comprehension".
 */
export const ENGAGEMENT_REASONS = [
  'inactive',
  'behind_cohort_pace',
  'low_first_attempt_quiz',
  'quiz_on_track',
  'never_started',
] as const;
export type EngagementReason = (typeof ENGAGEMENT_REASONS)[number];

/** Why an item-level statistic is worth an author's attention. */
export const ITEM_STAT_FLAGS = [
  /** Most first-attempt learners get it wrong — the A4 headline signal. */
  'low_p_value',
  /** Nearly everyone gets it right; it discriminates nothing. */
  'ceiling',
  /** Weak/negative correlation with overall performance — a suspect item. */
  'poor_discrimination',
  /** A distractor nobody chose: the item is effectively shorter than it looks. */
  'unused_distractor',
] as const;
export type ItemStatFlag = (typeof ITEM_STAT_FLAGS)[number];

/**
 * Telemetry ingest bounds. Both exist because `occurredAt` and `durationMs` are
 * CLIENT-SUPPLIED, and a client is not a trusted clock.
 *
 * An event claiming to have happened in 2099 poisons every recency read (the
 * learner is permanently "active"); a heartbeat claiming ten hours poisons
 * time-on-site, which is the lead score itself. Both are clamped rather than
 * rejected — a buggy or clock-skewed client would otherwise lose an entire
 * batch of genuine events — and the response reports the count so the
 * distortion is visible rather than silent.
 */
export const MAX_EVENT_DURATION_MS = 5 * 60 * 1000;
/** How far into the past a client may date an event (offline buffering). */
export const MAX_EVENT_BACKDATE_MS = 7 * 24 * 60 * 60 * 1000;
/** Events per ingest request. Beacons batch; they do not bulk-upload. */
export const MAX_EVENT_BATCH = 100;

/** Default reporting window. A term is longer, but a demo tenant is days old. */
export const DEFAULT_REPORT_WINDOW_DAYS = 30;

/**
 * Default span of the weekly-active series, in WEEKS — a quarter, which is
 * long enough for a launch-then-adoption shape to be visible and short enough
 * that the newest bar is still legible next to the oldest.
 */
export const DEFAULT_REPORT_WEEKS = 12;

/**
 * Where a tutor corpus chunk's text CAME FROM — the provenance the rights gate
 * is applied to (issue #12, and the rights posture in
 * docs/publish-contract.md §8).
 *
 * `component` is course-owned text: Cusp commissioned it, Cusp may serve it.
 * `abstract` and `third_party` are ingested source material the team may HOLD
 * for internal tagging without holding the right to SERVE to a paying
 * subscriber — two different legal questions. They exist in the vocabulary now
 * so ingested chunks are labelled from day one rather than guessed at later.
 */
export const TUTOR_SOURCE_KINDS = ['component', 'abstract', 'third_party'] as const;
export type TutorSourceKind = (typeof TUTOR_SOURCE_KINDS)[number];

/**
 * THE RIGHTS GATE, as an allowlist. Only kinds listed here may be retrieved
 * into an answer — everything else, including any kind added later, is
 * unservable until it is deliberately added. The dashboard's rule that
 * `unknown` is never read as permission becomes structural at this boundary:
 * there is no unknown to misread, only membership in this list.
 *
 * v1 ships course-owned text only (decided on issue #12). Widening this list
 * is a per-source legal clearance, not a code change to make lightly.
 */
export const TUTOR_SERVABLE_SOURCE_KINDS: readonly TutorSourceKind[] = ['component'] as const;

/**
 * How a tutor turn ended. `refused` is a first-class outcome, not an error:
 * "I don't have that" on an out-of-scope question (Chris's implant-torque
 * probe) is the behaviour being sold, and recording it is what lets the corpus
 * gap analysis exist later.
 */
export const TUTOR_TURN_OUTCOMES = ['answered', 'refused'] as const;
export type TutorTurnOutcome = (typeof TUTOR_TURN_OUTCOMES)[number];

/**
 * THE DASHBOARD'S TUTOR VOCABULARY, mirrored (cusp-dashboard@504f2b6,
 * drizzle/schema.ts `tutor_sessions` / `tutor_turns` / `tutor_turn_evidence` /
 * `question_tutor_variants`; docs/tutor-contract.md carries the rules each
 * value encodes). These are `text` columns narrowed by CHECK constraints in
 * the database and by these unions in TypeScript — the dashboard's own
 * pattern, kept so a value added there is a one-line change here.
 */

/**
 * The three kinds of conversation (rule 30). `practice` anchors on a question
 * and is the only kind the dashboard lets bank mastery — cusp grades mastery
 * from the quiz instead (decision 2026-09-11), so here it is the only kind
 * that GRADES an answer. The two discussion kinds are exploring, never
 * demonstrating (rule 47).
 */
export const TUTOR_SESSION_KINDS = ['practice', 'lecture_discussion', 'course_discussion'] as const;
export type TutorSessionKind = (typeof TUTOR_SESSION_KINDS)[number];

/**
 * Who is driving (rule 42). `eval` is the founders' probe harness: it bypasses
 * gating and is excluded from every learner record and every analytic, so the
 * only thing claiming it buys is LESS credit.
 */
export const TUTOR_SESSION_SOURCES = ['learner', 'eval'] as const;
export type TutorSessionSource = (typeof TUTOR_SESSION_SOURCES)[number];

export const TUTOR_SESSION_STATUSES = ['active', 'completed', 'abandoned'] as const;
export type TutorSessionStatus = (typeof TUTOR_SESSION_STATUSES)[number];

/** How the learner answers in a practice session — free text natively, or an open answer over an MC stem. */
export const TUTOR_ANSWER_MODES = ['free_text_native', 'open_answer_over_mc'] as const;
export type TutorAnswerMode = (typeof TUTOR_ANSWER_MODES)[number];

/** One row per utterance (rule 37): the learner's words verbatim, or the VERIFIED tutor reply. */
export const TUTOR_TURN_ROLES = ['learner', 'tutor'] as const;
export type TutorTurnRole = (typeof TUTOR_TURN_ROLES)[number];

/**
 * How a tutor turn cleared the truth gate (rules 5–8, 31). `grounded` passed
 * verification first time; `revised` after the one repair; `refused` is the
 * honest failure mode and carries ZERO evidence rows; `seeded` is a
 * deterministic template over ratified records (the discussion opener) that
 * never went through the verifier — the distinct value exists so no surface
 * can mistake it for a verified reply.
 */
export const TUTOR_VERDICTS = ['grounded', 'revised', 'refused', 'seeded'] as const;
export type TutorVerdict = (typeof TUTOR_VERDICTS)[number];

/** The wire outcome a verdict collapses to — `refused` is the only refusal. */
export function tutorOutcomeOf(verdict: TutorVerdict): TutorTurnOutcome {
  return verdict === 'refused' ? 'refused' : 'answered';
}

/** The grade of a learner's practice answer, set on the grading turn (rule 47 signal only). */
export const TUTOR_ANSWER_ASSESSMENTS = ['correct', 'close', 'needs_more', 'off_track'] as const;
export type TutorAnswerAssessment = (typeof TUTOR_ANSWER_ASSESSMENTS)[number];

/** How a receipt's passage was found. cusp serves `fts` today; `pinned` is the practice answer key (rule 16). */
export const TUTOR_RETRIEVAL_METHODS = ['vector', 'fts', 'fused', 'pinned'] as const;
export type TutorRetrievalMethod = (typeof TUTOR_RETRIEVAL_METHODS)[number];

/**
 * The render-time overlay on an MC question (rule 34): `open` = answer in your
 * own words, `defend` = pick an option then defend it in writing, `mc_only` =
 * the tutor never asks it. Package-fed from the dashboard's founder-ratified
 * `question_tutor_variants`; the app only reads it.
 */
export const TUTOR_VARIANT_CLASSES = ['open', 'defend', 'mc_only'] as const;
export type TutorVariantClass = (typeof TUTOR_VARIANT_CLASSES)[number];

/** Who set a variant: a model proposal, or a founder override that re-runs never overwrite. */
export const TUTOR_VARIANT_SETTERS = ['model', 'founder'] as const;
export type TutorVariantSetter = (typeof TUTOR_VARIANT_SETTERS)[number];

/**
 * Per-course practice gating (rules 39–41). `nudge` never blocks; `locked` is
 * enforced server-side on practice only and degrades to nudge when the lecture
 * has no measurable media; discussion is never hard-locked.
 */
export const QUIZ_GATING_MODES = ['open', 'nudge', 'locked'] as const;
export type QuizGatingMode = (typeof QUIZ_GATING_MODES)[number];
/** The dashboard's name for the same type — what the lifted gating.ts imports (issue #273). */
export type QuizGatingPolicy = QuizGatingMode;
export const DEFAULT_QUIZ_GATING: QuizGatingMode = 'nudge';

/**
 * A lecture's journey state as the dashboard's gating policy reads it
 * (cusp-dashboard journey.ts @ 504f2b6). The journey surface itself is
 * deferred (lift map: second plan); the lifted gating.ts needs only the type.
 */
export const JOURNEY_PROGRESS_STATES = ['not_started', 'in_progress', 'completed'] as const;
export type JourneyProgressState = (typeof JOURNEY_PROGRESS_STATES)[number];

/**
 * Per-membership tutor quota (D5, 2026-07-28). Sized to normal HUMAN usage,
 * not bot defense — access is identified (#19), so the auth requirement does
 * the heavy lifting and this is the backstop. D4 estimates 100–1,000 requests
 * per student per COURSE; a few hundred per DAY is far above any real student
 * and absurd for a bot fleet. The daily limit is a rolling 24h count of the
 * membership's recorded turns; the burst limit caps a runaway loop within a
 * minute. Both answer 429 with a friendly retry, never a captcha.
 */
export const TUTOR_DAILY_TURN_LIMIT = 300;
export const TUTOR_BURST_TURN_LIMIT = 20;
export const TUTOR_BURST_WINDOW_MS = 60_000;

/** Prospect tutor backstops, separate from paying learners' quotas. */
export const PROSPECT_TUTOR_DAILY_TURN_LIMIT = 40;
export const PROSPECT_TUTOR_BURST_TURN_LIMIT = 5;
export const PROSPECT_TUTOR_SESSION_TURN_LIMIT = 20;
export const PROSPECT_TUTOR_DAILY_SESSION_LIMIT = 10;
export const PROSPECT_TURN_COST_RESERVE_USD = 0.05;

/** Bounds on what one turn may carry. */
export const TUTOR_MAX_QUESTION_CHARS = 2_000;
export const TUTOR_MAX_FLAG_REASON_CHARS = 500;

/** Passages retrieved per question. A tight corpus is the refusal guarantee. */
export const TUTOR_RETRIEVAL_LIMIT = 6;

/**
 * The AI disclaimer served WITH every answer, so no client can render a tutor
 * response without it (the serializer ships it; forgetting it in a UI is a
 * missing string, not a missing legal surface). PROVISIONAL WORDING: the final
 * text is a lawyer question tracked with Chris in the open-actions ledger
 * (issue #12, "Real Vision carries one") — revising this constant is the whole
 * change when it lands.
 */
export const TUTOR_DISCLAIMER =
  'AI-generated study help. Answers are drawn from this course’s own material and cite it, ' +
  'but may contain mistakes — verify anything you rely on against the lecture or your instructor.';

/** Prior turns of the same (membership, lecture) thread shown to the model. */
export const TUTOR_CONTEXT_TURNS = 6;

/**
 * The tenant/user context resolved for a request. Used to set the Postgres
 * RLS session variables so every query is automatically tenant-scoped.
 */
export interface TenantContext {
  tenantId: string;
  userId: string;
  role: Role;
}

/**
 * The two brand DIRECTIONS — Obsidian (dark, the standard mode) and Canvas
 * (light). Decided 2026-07-27 (issue #16); the values themselves live in
 * `packages/theme/src/styles.css`, selected by `data-theme` on <html>.
 *
 * It lives HERE rather than in `@cusp/theme` because it is about to be a wire
 * value as well as a stylesheet one: a school's brand carries a default
 * direction, so `@cusp/contracts` needs the enum — and contracts may depend on
 * `@cusp/core` only, never on a package that imports CSS and React.
 * `@cusp/theme` re-exports it, so every existing `import { THEMES } from
 * '@cusp/theme'` keeps working and there is still exactly one list.
 */
export const THEMES = ['obsidian', 'canvas'] as const;
export type Theme = (typeof THEMES)[number];
